import {
  ConnectionState,
  type RemoteParticipant,
  type Room,
  RoomEvent,
} from 'livekit-client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CHAT_TOPIC,
  type ChatMsg,
  type RtRole,
  decodeChatMsg,
  encodeChatMsg,
  parseParticipantMetadata,
} from '../lib/rt-protocol'

/** Ring buffer cap — oldest messages drop past this. */
export const CHAT_HISTORY_LIMIT = 500

export interface ChatEntry {
  /** local monotonically increasing id (render key) */
  id: number
  kind: 'chat' | 'system'
  /** sender identity ('' for system rows) */
  from: string
  role: RtRole | 'unknown'
  name: string
  text: string
  /** wall-clock ms */
  ts: number
  /** private target identity (private messages only) */
  to?: string
  /** resolved display name of the private target */
  toName?: string
  isLocal: boolean
}

export interface ChatRecipient {
  identity: string
  role: RtRole | 'unknown'
  name: string
}

export interface UseChatOptions {
  /**
   * LiveKit Room instance (pass null in mock/offline mode — chat then works
   * local-only). Provided as a prop on purpose: this hook never creates or
   * owns the room (that stays in use-rt-room).
   */
  room: Room | null
  /** our LiveKit identity (room.localParticipant.identity once connected) */
  localIdentity: string
  localRole: RtRole | 'unknown'
  localName: string
  /** start with the panel expanded (default false = collapsed) */
  initialOpen?: boolean
}

export interface UseChatResult {
  /** newest last, capped at CHAT_HISTORY_LIMIT */
  messages: ChatEntry[]
  /** remote chat messages received while the panel was collapsed */
  unread: number
  open: boolean
  setOpen: (open: boolean) => void
  toggle: () => void
  /** current "send to" target identity; '' = everyone */
  target: string
  setTarget: (identity: string) => void
  /** open the panel pre-targeted at one participant (SLP per-user chat button) */
  openTo: (identity: string) => void
  /** online remote participants for the "send to" dropdown */
  recipients: ChatRecipient[]
  /** publish a message; toIdentity ⇒ private via destinationIdentities */
  send: (text: string, toIdentity?: string) => void
  /** inject a gray system row (join/leave/REC events — fed by the caller) */
  addSystem: (text: string) => void
  /** formatted transcript (timestamps + senders) for the .txt export */
  exportText: () => string
}

const ROLE_ORDER: Record<string, number> = { studio: 0, director: 1, engineer: 2, actor: 3, unknown: 4 }

function fmtClock(ts: number): string {
  const d = new Date(ts)
  const p = (n: number) => (n < 10 ? `0${n}` : String(n))
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/**
 * Zoom-style chat over LiveKit data channel topic 'chat'.
 * In-memory only (v1) — no persistence; export via exportText().
 */
export function useChat(opts: UseChatOptions): UseChatResult {
  const { room, localIdentity, localRole, localName, initialOpen = false } = opts

  const [messages, setMessages] = useState<ChatEntry[]>([])
  const [unread, setUnread] = useState(0)
  const [open, setOpenState] = useState(initialOpen)
  const [target, setTarget] = useState('')
  const [recipients, setRecipients] = useState<ChatRecipient[]>([])

  const openRef = useRef(open)
  const seqRef = useRef(0)
  const recipientsRef = useRef<ChatRecipient[]>([])
  recipientsRef.current = recipients
  const messagesRef = useRef<ChatEntry[]>([])
  messagesRef.current = messages

  const pushEntry = useCallback((entry: Omit<ChatEntry, 'id'>) => {
    setMessages((prev) => {
      const next = prev.length >= CHAT_HISTORY_LIMIT ? prev.slice(prev.length - CHAT_HISTORY_LIMIT + 1) : prev.slice()
      next.push({ ...entry, id: ++seqRef.current })
      return next
    })
  }, [])

  const setOpen = useCallback((next: boolean) => {
    openRef.current = next
    setOpenState(next)
    if (next) setUnread(0)
  }, [])

  const toggle = useCallback(() => setOpen(!openRef.current), [setOpen])

  const openTo = useCallback(
    (identity: string) => {
      setTarget(identity)
      setOpen(true)
    },
    [setOpen],
  )

  const resolveToName = useCallback(
    (to: string | undefined): string | undefined => {
      if (!to) return undefined
      if (to === localIdentity) return localName
      return recipientsRef.current.find((r) => r.identity === to)?.name ?? to
    },
    [localIdentity, localName],
  )

  // ----------------------------------------------------------- room wiring
  useEffect(() => {
    if (!room) return

    const syncRecipients = () => {
      const list: ChatRecipient[] = []
      for (const p of room.remoteParticipants.values()) {
        const meta = parseParticipantMetadata(p.metadata)
        list.push({ identity: p.identity, role: meta?.role ?? 'unknown', name: meta?.name ?? p.identity })
      }
      list.sort((a, b) => (ROLE_ORDER[a.role] ?? 4) - (ROLE_ORDER[b.role] ?? 4) || a.name.localeCompare(b.name))
      setRecipients(list)
    }

    const onData = (payload: Uint8Array, participant?: RemoteParticipant, _kind?: unknown, topic?: string) => {
      if (topic !== CHAT_TOPIC) return
      const msg = decodeChatMsg(payload)
      if (!msg) return
      // Prefer the transport identity over the self-declared one (anti-spoof).
      const from = participant?.identity ?? msg.from
      pushEntry({
        kind: 'chat',
        from,
        role: msg.role,
        name: msg.name,
        text: msg.text,
        ts: msg.ts,
        ...(msg.to ? { to: msg.to, toName: resolveToName(msg.to) } : {}),
        isLocal: false,
      })
      if (!openRef.current) setUnread((n) => n + 1)
    }

    room
      .on(RoomEvent.DataReceived, onData)
      .on(RoomEvent.ParticipantConnected, syncRecipients)
      .on(RoomEvent.ParticipantDisconnected, syncRecipients)
      .on(RoomEvent.ParticipantMetadataChanged, syncRecipients)
      .on(RoomEvent.ConnectionStateChanged, syncRecipients)
    syncRecipients()

    return () => {
      room
        .off(RoomEvent.DataReceived, onData)
        .off(RoomEvent.ParticipantConnected, syncRecipients)
        .off(RoomEvent.ParticipantDisconnected, syncRecipients)
        .off(RoomEvent.ParticipantMetadataChanged, syncRecipients)
        .off(RoomEvent.ConnectionStateChanged, syncRecipients)
    }
  }, [pushEntry, resolveToName, room])

  // Drop a stale private target if that participant left.
  useEffect(() => {
    if (target && !recipients.some((r) => r.identity === target)) setTarget('')
  }, [recipients, target])

  // ------------------------------------------------------------------- api
  const send = useCallback(
    (text: string, toIdentity?: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      const to = toIdentity || undefined
      const msg: ChatMsg = {
        t: 'chat',
        from: localIdentity,
        role: localRole,
        name: localName,
        text: trimmed,
        ts: Date.now(),
        ...(to ? { to } : {}),
      }
      if (room && room.state === ConnectionState.Connected) {
        void room.localParticipant
          .publishData(encodeChatMsg(msg), {
            reliable: true,
            topic: CHAT_TOPIC,
            ...(to ? { destinationIdentities: [to] } : {}),
          })
          .catch(() => undefined)
      }
      // LiveKit does not echo data to the sender — append the local copy.
      pushEntry({
        kind: 'chat',
        from: localIdentity,
        role: localRole,
        name: localName,
        text: trimmed,
        ts: msg.ts,
        ...(to ? { to, toName: resolveToName(to) } : {}),
        isLocal: true,
      })
    },
    [localIdentity, localName, localRole, pushEntry, resolveToName, room],
  )

  const addSystem = useCallback(
    (text: string) => {
      pushEntry({
        kind: 'system',
        from: '',
        role: 'unknown',
        name: 'system',
        text,
        ts: Date.now(),
        isLocal: false,
      })
    },
    [pushEntry],
  )

  const exportText = useCallback((): string => {
    const lines = messagesRef.current.map((m) => {
      if (m.kind === 'system') return `[${fmtClock(m.ts)}] * ${m.text}`
      const priv = m.to ? ` -> ${m.toName ?? m.to} (private)` : ''
      return `[${fmtClock(m.ts)}] ${m.name} (${m.role})${priv}: ${m.text}`
    })
    return `${lines.join('\n')}\n`
  }, [])

  return useMemo(
    () => ({
      messages,
      unread,
      open,
      setOpen,
      toggle,
      target,
      setTarget,
      openTo,
      recipients,
      send,
      addSystem,
      exportText,
    }),
    [messages, unread, open, setOpen, toggle, target, openTo, recipients, send, addSystem, exportText],
  )
}
